import React, { useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useParams } from "react-router-dom";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ButtonBackPage from "@components/ButtonBackPage";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { ActionsType } from "src/interfaces/Common";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const UsersWating: React.FC<{
  //type: LOType;
  //page: PagesID;
}> = (/*{ type, page }*/) => {
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    key?: string | number;
  }>();

  const { id, idLO, idEdition } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.catalog.users-waiting"]}`;

  const { t } = useTranslation(["users-waiting-page", "toast-message"]);

  const { columns, dataTable, actionForRow, mutate } = useFetchTable(
    `${ServicesURL.getUsersWaiting}?corporateId=${+id! || 1}${
      idLO ? `&objectId=${idLO}` : ``
    }${idEdition ? `&editionId=${idEdition}` : ``}`,
    PagesID["corporate.catalog.users-waiting"],
    filters[filterId],
  );

  const { trigger: triggerApproveDecline } = useSWRMutation(
    `${ServicesURL.patchUsersApproveDeny}`,
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(`${url}`, MethodType.PATCH, { ...arg.arg });
    },
  );

  const hasNoSeatsMessage = (value: any) => {
    if (!value) return false;
    if (typeof value === "string") return value.includes("no seats available");
    if (typeof value === "object") {
      const possibleMessages = [
        value.message,
        value.error,
        value.detail,
        value.title,
      ];
      return possibleMessages.some((msg) =>
        typeof msg === "string" ? msg.includes("no seats available") : false,
      );
    }
    return false;
  };

  const onSubmit = async (outcome: string, row: any) => {
    let objToSend = {
      studentEid: row.userId,
      enrollDate: new Date(),
      approve: outcome,
      learningObject: {
        corporate_id: +id! || 1,
        object_id: row.objectId,
        edition_id: row.editionId,
      },
    };
    try {
      const response = await triggerApproveDecline(objToSend);

      if (hasNoSeatsMessage(response)) {
        setOpenToast({
          s: StatusEnum.KO,
          message: t("toast-message:error-no-seats"),
        });
      } else {
        setOpenToast({ s: StatusEnum.OK, message: "" });
      }
    } catch (error: any) {
      console.error(error);
      setOpenToast(undefined);
    }
    mutate && mutate();
  };

  const configurationActionForRow: ActionsType[] | undefined =
    React.useMemo(() => {
      return actionForRow?.map((action: any) => {
        if (action.type === "button") {
          if (action.icon === "approve") {
            return {
              ...action,
              disableIf: [...(action.disableIf || []), "editionOverbooked"],
              callBack: (row: any) => {
                onSubmit("Y", row);
              },
            };
          } else {
            return {
              ...action,
              callBack: (row: any) => {
                onSubmit("N", row);
              },
            };
          }
        }
        return {
          ...action,
        };
      });
    }, [actionForRow]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-courses.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <ButtonBackPage />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={PagesID["corporate.catalog.users-waiting"]}
              data={
                dataTable && dataTable.enrollmentResponses
                  ? dataTable.enrollmentResponses.map((response: any) => {
                      return {
                        ...response,
                        ...response.learningObject,
                        learningObject: null,
                      };
                    })
                  : []
              }
              columns={columns || []}
              configurationTopToolbar={[]}
              enableRowSelection={false}
              enableRowActions={true}
              configurationActionForRow={configurationActionForRow}
              totalPages={dataTable?.metadata?.totalElements}
            />
          </Box>
        )}
        <ModalConfirmsCst
          open={!!openToast}
          title={openToast?.s === StatusEnum.KO ? "" : t("hero-title-add")}
          description={""}
          onCallBackClosed={() => {
            setOpenToast(undefined);
          }}
          status={openToast}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default UsersWating;
